import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';

import { ThunkHttpClient } from '../utils/httpThunkClient';
// Types
export interface WhatsAppAccount {
  id: number;
  phoneNumber: string;
  displayName: string;
  status: string;
  unreadCount: number;
}

export interface Conversation {
  customerId: number;
  customerName: string;
  waNumber: string;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
  lastMessageType: string;
}

export interface Message {
  id: number;
  messageId: string;
  direction: 'IN' | 'OUT';
  type: string;
  textBody: string;
  mediaUrl?: string;
  status: string;
  timestamp: string;
  isRead: boolean;
}

export interface SendMessageRequest {
  type: string;
  textBody: string;
  mediaUrl?: string;
  caption?: string;
}

export interface UnreadCount {
  accountId: number;
  totalUnreadCount: number;
}

interface WhatsAppState {
  accounts: WhatsAppAccount[];
  conversations: Conversation[];
  messages: Message[];
  unreadCounts: { [accountId: number]: number };
  loading: boolean;
  error: string | null;
  selectedAccount: number | null;
  selectedCustomer: Conversation | null;
}

const initialState: WhatsAppState = {
  accounts: [],
  conversations: [],
  messages: [],
  unreadCounts: {},
  loading: false,
  error: null,
  selectedAccount: null,
  selectedCustomer: null,
};


// export const saveBookingDraft = ThunkHttpClient.post('bookingslice/savedraftbooking',
//     (data) => { return { url: "booking", body:  data} })  
    
//     export const updateBooking = ThunkHttpClient.put('bookingslice/updatebooking',
//     ({id, data}) => { return { url: "booking/" + id, body:  data} })
    
//     export const getBookingDraft  = ThunkHttpClient.get('bookingslice/getbookingdraft',
//      ()  =>  "booking/draft")

//     export const getBookingDraftById  = ThunkHttpClient.get('bookingslice/getbookingdraftbyid',
//       (id)   =>  "booking/"+id)
// Async thunks
export const fetchAccounts = ThunkHttpClient.get(
  'whatsapp/fetchAccounts',
  (id)   =>  "/api/whatsapp/accounts")

export const fetchConversations = ThunkHttpClient.get(
  'whatsapp/fetchConversations',
  (accountId)   =>  "/api/whatsapp/accounts/"+accountId+"/conversations")

export const fetchMessages = ThunkHttpClient.get('whatsapp/fetchMessages',
  ({ accountId, customerId, page = 0, size = 50 }: { accountId: number; customerId: number; page?: number; size?: number }) => (
     "/api/whatsapp/accounts/"+accountId+"/conversations/"+customerId+"/messages?page="+page+"&size="+size)
);

export const sendMessage = ThunkHttpClient.put  (
  'whatsapp/sendMessage',
  ({ accountId, customerId, message }: { accountId: number; customerId: number; message: SendMessageRequest }) =>
    
    { return { url: "/api/whatsapp/accounts/"+accountId+"/conversations/"+customerId+"/messages", body:  message} })  

export const markAsRead = ThunkHttpClient.put(
  'whatsapp/markAsRead',    
  ({ accountId, customerId }: { accountId: number; customerId: number }) =>
    { return { url: "/api/whatsapp/accounts/"+accountId+"/conversations/"+customerId+"/read", body: undefined } })  

export const fetchUnreadCount = ThunkHttpClient.get(
  'whatsapp/fetchUnreadCount',
  (accountId: number) => ("/api/whatsapp/accounts/"+accountId+"/unread-count"))

// Slice
const whatsappSlice = createSlice({
  name: 'whatsapp',
  initialState,
  reducers: {
    setSelectedAccount: (state, action: PayloadAction<number | null>) => {
      state.selectedAccount = action.payload;
      state.selectedCustomer = null;
      state.messages = [];
    },
    setSelectedCustomer: (state, action: PayloadAction<Conversation | null>) => {
      state.selectedCustomer = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    addMessage: (state, action: PayloadAction<Message>) => {
      state.messages.unshift(action.payload);
    },
    updateMessageStatus: (state, action: PayloadAction<{ messageId: string; status: string }>) => {
      const message = state.messages.find(m => m.messageId === action.payload.messageId);
      if (message) {
        message.status = action.payload.status;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch accounts
    builder
      .addCase(fetchAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.accounts = action.payload.data || [];
        // Update unread counts
        state.accounts.forEach((account: WhatsAppAccount) => {
          state.unreadCounts[account.id] = account.unreadCount;
        });
      })
      .addCase(fetchAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch accounts';
      });

    // Fetch conversations
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = action.payload.data;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch conversations';
      });

    // Fetch messages
    builder
      .addCase(fetchMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload.data.reverse(); // Reverse to show oldest first
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch messages';
      });

    // Send message
    builder
      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.messages.push(action.payload);
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to send message';
      });

    // Mark as read
    builder
      .addCase(markAsRead.fulfilled, (state, action) => {
        const { accountId, customerId } = action.payload;
        
        // Update conversation unread count
        const conversation = state.conversations.find(c => c.customerId === customerId);
        if (conversation) {
          conversation.unreadCount = 0;
        }
        
        // Update account unread count
        if (state.unreadCounts[accountId]) {
          state.unreadCounts[accountId] = Math.max(0, state.unreadCounts[accountId] - (conversation?.unreadCount || 0));
        }
        
        // Mark messages as read
        state.messages.forEach(message => {
          if (message.direction === 'IN') {
            message.isRead = true;
          }
        });
      });

    // Fetch unread count
    builder
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        const { accountId, totalUnreadCount } = action.payload;
        state.unreadCounts[accountId] = totalUnreadCount;
      });
  },
});

export const {
  setSelectedAccount,
  setSelectedCustomer,
  clearError,
  addMessage,
  updateMessageStatus,
} = whatsappSlice.actions;

// Selectors
export const selectAccounts = (state: { whatsapp: WhatsAppState }) => state.whatsapp.accounts;
export const selectConversations = (state: { whatsapp: WhatsAppState }) => state.whatsapp.conversations;
export const selectMessages = (state: { whatsapp: WhatsAppState }) => state.whatsapp.messages;
export const selectUnreadCounts = (state: { whatsapp: WhatsAppState }) => state.whatsapp.unreadCounts;
export const selectLoading = (state: { whatsapp: WhatsAppState }) => state.whatsapp.loading;
export const selectError = (state: { whatsapp: WhatsAppState }) => state.whatsapp.error;
export const selectSelectedAccount = (state: { whatsapp: WhatsAppState }) => state.whatsapp.selectedAccount;
export const selectSelectedCustomer = (state: { whatsapp: WhatsAppState }) => state.whatsapp.selectedCustomer;

// Hook for easy access
export const useWhatsApp = () => {
  const dispatch = useDispatch<any>();
  const accounts = useSelector(selectAccounts);
  const conversations = useSelector(selectConversations);
  const messages = useSelector(selectMessages);
  const unreadCounts = useSelector(selectUnreadCounts);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const selectedAccount = useSelector(selectSelectedAccount);
  const selectedCustomer = useSelector(selectSelectedCustomer);

  return {
    accounts,
    conversations,
    messages,
    unreadCounts,
    loading,
    error,
    selectedAccount,
    selectedCustomer,
    fetchAccounts: () => dispatch(fetchAccounts()),
    fetchConversations: (accountId: number) => dispatch(fetchConversations(accountId)),
    fetchMessages: (accountId: number, customerId: number, page?: number, size?: number) => 
      dispatch(fetchMessages({ accountId, customerId, page, size })),
    sendMessage: (accountId: number, customerId: number, message: SendMessageRequest) => 
      dispatch(sendMessage({ accountId, customerId, message })),
    markAsRead: (accountId: number, customerId: number) => 
      dispatch(markAsRead({ accountId, customerId })),
    fetchUnreadCount: (accountId: number) => dispatch(fetchUnreadCount(accountId)),
    setSelectedAccount: (accountId: number | null) => dispatch(setSelectedAccount(accountId)),
    setSelectedCustomer: (customer: Conversation | null) => dispatch(setSelectedCustomer(customer)),
    clearError: () => dispatch(clearError()),
  };
};

export default whatsappSlice.reducer;
